import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PricingHistoryPanel = ({
  priceHistory = [],
  currentPrice = 0,
  productName = '',
  className = ''
}) => {
  if (priceHistory?.length === 0) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="text-center text-muted-foreground">
          <Icon name="TrendingUp" size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No pricing history available</p>
        </div>
      </div>
    );
  }

  const formatPrice = (price) => `$${price?.toFixed(2)}`;
  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const minPrice = Math.min(...priceHistory?.map(p => p?.price));
  const maxPrice = Math.max(...priceHistory?.map(p => p?.price));
  const priceChange = priceHistory?.length > 1 
    ? ((priceHistory?.[priceHistory?.length - 1]?.price - priceHistory?.[0]?.price) / priceHistory?.[0]?.price) * 100
    : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-modal">
          <p className="text-sm font-medium text-foreground">
            {formatDate(label)}
          </p>
          <p className="text-sm text-primary">
            Price: {formatPrice(payload?.[0]?.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-foreground">
          Pricing History
        </h3>
        <div className="flex items-center space-x-2">
          <Icon 
            name={priceChange >= 0 ? 'TrendingUp' : 'TrendingDown'} 
            size={16} 
            className={priceChange >= 0 ? 'text-success' : 'text-error'}
          />
          <span className={`text-xs font-medium ${
            priceChange >= 0 ? 'text-success' : 'text-error'
          }`}>
            {priceChange >= 0 ? '+' : ''}{priceChange?.toFixed(1)}%
          </span>
        </div>
      </div>
      {/* Price Statistics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-2 bg-muted bg-opacity-50 rounded">
          <div className="text-sm font-semibold text-foreground">
            {formatPrice(currentPrice)}
          </div>
          <div className="text-xs text-muted-foreground">
            Current
          </div>
        </div>
        
        <div className="text-center p-2 bg-success bg-opacity-10 rounded">
          <div className="text-sm font-semibold text-success">
            {formatPrice(minPrice)}
          </div>
          <div className="text-xs text-muted-foreground">
            Lowest
          </div>
        </div>
        
        <div className="text-center p-2 bg-error bg-opacity-10 rounded">
          <div className="text-sm font-semibold text-error">
            {formatPrice(maxPrice)}
          </div>
          <div className="text-xs text-muted-foreground">
            Highest
          </div>
        </div>
      </div>
      {/* Price Chart */}
      <div className="h-32 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={priceHistory}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
            />
            <YAxis 
              tickFormatter={formatPrice}
              tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="price" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 3 }}
              activeDot={{ r: 4, stroke: 'var(--color-primary)', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Recent Price Changes */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Recent Changes
        </h4>
        <div className="space-y-1 max-h-20 overflow-y-auto">
          {priceHistory?.slice(-3)?.reverse()?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {formatDate(entry?.date)}
              </span>
              <span className="font-medium text-foreground">
                {formatPrice(entry?.price)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingHistoryPanel;